import React, { useCallback, useState } from 'react'
import { AppShell } from '../layout/AppShell'
import { graphql } from '../../gql'
import { useQuery } from 'urql'
import { useUser } from '../../context/UserContext'
import { Co2Chart } from './Co2Chart'
import SecondaryButton from '../../components/Buttons/SecondaryButton'
import { Cog8ToothIcon } from '@heroicons/react/20/solid'
import { AnalyticsParametersModal } from './AnalyticsParametersModal'
import { PeriodOption } from './types'
import { API_URL } from '../../utils/env'
import { getAuthorizationToken } from '../authentication/storage'
import queryString from 'query-string'

const DashboardAnalyticsQuery = graphql(`
  query DashboardAnalytics(
    $businessId: ID!
    $periodStart: DateTime
    $periodEnd: DateTime
  ) {
    dashboardAnalytics(
      businessId: $businessId
      periodStart: $periodStart
      periodEnd: $periodEnd
    ) {
      totalBags
      parcelsCycled
      totalCo2Reduction
      totalWasteReduction
      monthlyStats {
        month
        co2Reduction
        wasteReduction
      }
    }
  }
`)

export const Analytics: React.FC = () => {
  const user = useUser()
  const [period, setPeriod] = useState<PeriodOption>(undefined)
  const [parametersModalOpen, setParametersModalOpen] = useState(false)

  const [{ data }, refetch] = useQuery({
    query: DashboardAnalyticsQuery,
    variables: {
      businessId: user?.business?.id ?? '',
      periodStart: period?.periodStart,
      periodEnd: period?.periodEnd,
    },
    requestPolicy: 'cache-and-network',
  })

  const stats = [
    {
      name: 'Parcels cycled',
      stat: (data?.dashboardAnalytics?.parcelsCycled ?? 0).toFixed(0),
    },
    {
      name: 'Total waste reduction',
      stat:
        (data?.dashboardAnalytics?.totalWasteReduction ?? 0).toFixed(2) + ' kg',
    },
    {
      name: 'Total CO₂ reduction',
      stat:
        (data?.dashboardAnalytics?.totalCo2Reduction ?? 0).toFixed(2) + ' kCO₂',
    },
  ]

  const handleExport = useCallback(() => {
    if (!user?.business?.id) {
      console.error('No business id found for user, cannot export')
      return
    }

    const authorizationToken = getAuthorizationToken()

    if (!authorizationToken) {
      console.error(
        'No authorization token found, must be logged in to export.'
      )
      return
    }

    const params = {
      authorization_token: authorizationToken,
      period_start: period?.periodStart?.toISOString() ?? undefined,
      period_end: period?.periodEnd?.toISOString() ?? undefined,
    }

    console.log(queryString.stringify(params))
    const url = `${API_URL}/analytics/export/${
      user.business.id
    }?${queryString.stringify(params)}`

    window.open(url, '_blank')
  }, [user, period])

  return (
    <AppShell>
      <div className={'px-4 md:px-0'}>
        <div className="flex justify-between">
          <h1 className={'text-3xl leading-9 font-bold mb-4'}>Analytics</h1>
          <div>
            <SecondaryButton
              className={'flex gap-2'}
              onClick={() => setParametersModalOpen(true)}
            >
              Set parameters
              <Cog8ToothIcon width={'20px'} height={'20px'} />
            </SecondaryButton>
          </div>
        </div>
        <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
          {stats.map(item => (
            <div
              key={item.name}
              className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6"
            >
              <dt className="truncate text-sm font-medium text-gray-500">
                {item.name}
              </dt>
              <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900">
                {item.stat}
              </dd>
            </div>
          ))}
        </dl>
        <Co2Chart
          period={period}
          monthlyReductions={
            (data?.dashboardAnalytics?.monthlyStats
              ?.filter(x => !!x?.co2Reduction)
              .map(x => x!.co2Reduction!) as number[]) ?? []
          }
          onPeriodChange={setPeriod}
          onExport={handleExport}
        />
      </div>
      <AnalyticsParametersModal
        isOpen={parametersModalOpen}
        onUpdated={() => refetch({ requestPolicy: 'network-only' })}
        onClose={() => setParametersModalOpen(false)}
      />
    </AppShell>
  )
}
